import { Injectable, Injector, Inject, PLATFORM_ID } from '@angular/core';
import {
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpEvent,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable } from 'rxjs/Rx';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/internal/operators';
import { Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { StoreService } from './store.service';

/**
 * Globally guards secure pages: whenever an authenticated API call is rejected
 * because the session token is missing, corrupted, invalid or expired, the user
 * is logged out and sent to the sign-in page. Public requests (e.g. login) are
 * left untouched so their own error handling still runs.
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  // Public/auth routes that must never trigger a redirect loop.
  private readonly authPages = ['/sign-in', '/sign-up', '/forget-password', '/verify-account'];

  constructor(
    private injector: Injector,
    @Inject(PLATFORM_ID) private readonly platformId: any
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Only requests that carried our session token are session-bound. Failed
    // logins/registrations have no token header, so they won't force a logout.
    const hadAuthToken = req.headers.has('X-Access-Token') || req.headers.has('Authorization');

    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        if (isPlatformBrowser(this.platformId) && this.isSessionInvalid(error, hadAuthToken)) {
          this.forceLogout();
        }
        return throwError(error);
      })
    );
  }

  private isSessionInvalid(error: HttpErrorResponse, hadAuthToken: boolean): boolean {
    if (!error) {
      return false;
    }
    // 403 Forbidden: token was present but rejected as invalid/corrupted.
    if (error.status === 403) {
      return true;
    }
    // 401 Unauthorized only when the request was authenticated (skip public login failures).
    if (error.status === 401 && hadAuthToken) {
      return true;
    }
    // The API reports an expired token as 400 with a specific message.
    if (
      error.status === 400 &&
      error.error &&
      typeof error.error.message === 'string' &&
      error.error.message.toLowerCase().indexOf('token has been expired') !== -1
    ) {
      return true;
    }
    return false;
  }

  private forceLogout(): void {
    const store = this.injector.get(StoreService);
    const router = this.injector.get(Router);

    try {
      // Clears user + token from the store and localStorage.
      store.saveLoginData({ isLogged: false });
    } catch (e) {
      // no-op: still attempt the redirect below.
    }

    const currentUrl = (router.url || '').split('?')[0];
    const onAuthPage = this.authPages.some(page => currentUrl.indexOf(page) === 0);
    if (!onAuthPage) {
      router.navigateByUrl('sign-in');
    }
  }
}
